import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

const firebaseConfig = {
  apiKey:
    import.meta.env.VITE_FIREBASE_API_KEY ||
    "AIzaSyCBYXfMUdHE2l4oAjbEJ4F-YwVdnbqUbV0",
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    "studio-473406021-87ead.firebaseapp.com",
  projectId:
    import.meta.env.VITE_FIREBASE_PROJECT_ID || "studio-473406021-87ead",
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
    "studio-473406021-87ead.firebasestorage.app",
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "1070792785962",
  appId:
    import.meta.env.VITE_FIREBASE_APP_ID ||
    "1:1070792785962:web:eb58fb24b59c86d8a08353",
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);

// Gates the emulator wiring below AND selects the Firestore transport (see db).
const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";

// Firestore transport selection:
//  - PROD (no emulators): pass {} → the SDK default, which is already
//    `experimentalAutoDetectLongPolling` (firebase-js-sdk ≥ v10 defaults it to
//    true): fast streaming WebChannel where it works, auto long-polling
//    fallback behind restrictive proxies. Identical to the old getFirestore(app)
//    — PROD BEHAVIOR IS UNCHANGED.
//  - EMULATOR in a BROWSER (e2e/local dev): FORCE long-polling. Safari/WebKit
//    applies strict CORS to the emulator's streaming Write/Listen channel and
//    cancels it ("Load request cancelled" / "access control checks"). Auto-detect
//    does NOT rescue this: the initial channel handshake succeeds (so detection
//    keeps streaming) and only the subsequent Write POST is CORS-rejected, so the
//    SDK never abandons streaming. Forcing long-polling sidesteps the streaming
//    channel entirely. Long-polling is functionally equivalent on every engine,
//    so chromium/firefox/mobile e2e are unaffected (only the transport differs).
//  - EMULATOR in NODE (vitest integration suite: environment 'node',
//    VITE_USE_EMULATORS=true): pass {} — do NOT force long-polling. Forced LP in
//    Node intermittently mis-parses the emulator response
//    (RESOURCE_EXHAUSTED "Received message larger than max" → client offline →
//    flaky integration failures). The `typeof window` guard scopes the forcing
//    to real browsers, restoring the Node default transport that was always green.
// initializeFirestore() must run before any getFirestore(); on an HMR re-import
// with identical settings the SDK returns the existing instance (no throw), so
// the catch is a belt-and-braces fallback for an unexpected init error.
const forceEmulatorLongPolling =
  useEmulators && typeof window !== "undefined";
let db;
try {
  db = initializeFirestore(
    app,
    forceEmulatorLongPolling ? { experimentalForceLongPolling: true } : {}
  );
} catch {
  db = getFirestore(app);
}
const storage = getStorage(app);
const functions = getFunctions(app);
const emulatorHost = import.meta.env.VITE_EMULATOR_HOST || "127.0.0.1";
const authPort = Number(import.meta.env.VITE_AUTH_EMULATOR_PORT || 9099);
const firestorePort = Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080);
const storagePort = Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT || 9199);
const functionsPort = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || 5001);

// Startup guard: warn loudly if dev mode targets production without emulators.
// This prevents the silent foot-gun where a developer forgets env vars and
// corrupts production data.
if (import.meta.env.DEV && !useEmulators) {
  console.warn(
    "⚠️ Dev server is targeting PRODUCTION Firebase — emulator env not set. Did you mean VITE_USE_EMULATORS=true?"
  );
}

if (useEmulators && !globalThis.__QS_EMULATORS_CONNECTED__) {
  connectAuthEmulator(auth, `http://${emulatorHost}:${authPort}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, emulatorHost, firestorePort);
  connectStorageEmulator(storage, emulatorHost, storagePort);
  connectFunctionsEmulator(functions, emulatorHost, functionsPort);
  globalThis.__QS_EMULATORS_CONNECTED__ = true;
}

export { app, auth, db, storage, functions };
